import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { useAuth } from '@/hooks/use-auth';
import { usePlatformAdmin } from '@/hooks/use-platform-admin';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { customerService } from '@/lib/packages';
import { VisitService } from '@/lib/visit-service';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, ArrowLeft, BarChart3, LogIn, Loader2, RefreshCw, UserCheck, XCircle } from 'lucide-react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface KnownVisit {
  id: string;
  customer_id: string;
  entered_at: string | null;
  created_at: string;
  updated_at: string;
  status: string | null;
  match_confidence: number | null;
}

interface VisitFace {
  id: string;
  visit_id: string;
  snapshot_path: string | null;
  quality_score: number | null;
  camera_id: string | null;
  created_at: string;
}

interface CustomerLite {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email?: string | null;
}

interface KnownCustomerRecord {
  visit: KnownVisit;
  customer?: CustomerLite;
  entryFace?: VisitFace;
  entryImageUrl?: string;
}

interface MonthlyTrendPoint {
  month: string;
  known: number;
  unknown: number;
}

interface LookalikeCandidate {
  customer_id: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_phone: string | null;
  similarity: number;
  template_id: string;
  template_snapshot_path: string | null;
  template_count?: number;
}

const MAX_CUSTOMER_RESULTS = 200;
const MAX_DIALOG_LOOKALIKE_SUGGESTIONS = 3;
const LOOKALIKE_MIN_SIMILARITY = 0.65;

const normalizeSearchText = (value: string | null | undefined): string => {
  if (!value) return '';
  return value
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizePhoneForSearch = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

const getLocalDateInputValue = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMonthLabel = (date: Date): string => {
  return date.toLocaleDateString('tr-TR', { month: 'short', year: '2-digit' });
};

const KnownCustomers = () => {
  const navigate = useNavigate();
  const { companyId, userRole, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const { isPlatformAdmin, loading: adminLoading } = usePlatformAdmin();
  const canAccessKnownCustomers = isPlatformAdmin || userRole === 'admin' || userRole === 'employee';

  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<KnownCustomerRecord[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(getLocalDateInputValue());
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyTrendPoint[]>([]);
  const [trendLoading, setTrendLoading] = useState(true);
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<KnownCustomerRecord | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingVisitId, setUpdatingVisitId] = useState<string | null>(null);
  const [dialogLookalikes, setDialogLookalikes] = useState<LookalikeCandidate[]>([]);
  const [dialogLookalikesLoading, setDialogLookalikesLoading] = useState(false);
  const [dialogLookalikeImageUrls, setDialogLookalikeImageUrls] = useState<Record<string, string>>({});
  const AUTO_LABEL_MAX_DELTA_MS = 10 * 1000;

  useEffect(() => {
    if (!authLoading && !adminLoading && !canAccessKnownCustomers) {
      toast({
        title: 'Erişim Reddedildi',
        description: 'Bu sayfaya erişim yetkiniz bulunmamaktadır.',
        variant: 'destructive',
      });
      navigate('/');
    }
  }, [authLoading, adminLoading, canAccessKnownCustomers, navigate, toast]);

  const getImageUrl = async (imagePath: string | null): Promise<string | undefined> => {
    if (!imagePath) return undefined;

    try {
      const { data, error } = await supabase.storage
        .from('faces')
        .createSignedUrl(imagePath, 3600);
      if (!error && data?.signedUrl) {
        return data.signedUrl;
      }
    } catch {
      // Snapshot may be deleted or inaccessible by policy.
    }

    return undefined;
  };

  const loadKnownCustomers = async () => {
    if (!companyId || !canAccessKnownCustomers) return;

    setLoading(true);
    try {
      // System-known visits are Facey detections that already have customer_id
      // and remain effectively untouched after insert.
      // Heuristic: updated_at is almost equal to created_at.
      const dayStart = new Date(`${selectedDate}T00:00:00`);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);

      const { data: visitsData, error: visitsError } = await (supabase as any)
        .from('visits')
        .select('id, customer_id, entered_at, created_at, updated_at, status, match_confidence')
        .eq('company_id', companyId)
        .not('customer_id', 'is', null)
        .eq('status', 'LABELED')
        .gte('created_at', dayStart.toISOString())
        .lt('created_at', dayEnd.toISOString())
        .order('entered_at', { ascending: false })
        .limit(500);

      if (visitsError) {
        throw new Error(visitsError.message);
      }

      const knownVisits = ((visitsData || []) as KnownVisit[]).filter((visit) => {
        const createdMs = new Date(visit.created_at).getTime();
        const updatedMs = new Date(visit.updated_at).getTime();
        if (!Number.isFinite(createdMs) || !Number.isFinite(updatedMs)) return false;
        return Math.abs(updatedMs - createdMs) <= AUTO_LABEL_MAX_DELTA_MS;
      });
      if (knownVisits.length === 0) {
        setRecords([]);
        return;
      }

      const visitIds = knownVisits.map((visit) => visit.id);
      const customerIds = Array.from(
        new Set(knownVisits.map((visit) => visit.customer_id).filter(Boolean))
      );

      const [facesResult, customersResult] = await Promise.all([
        (supabase as any)
          .from('visit_faces')
          .select('id, visit_id, snapshot_path, quality_score, camera_id, created_at')
          .in('visit_id', visitIds)
          .order('created_at', { ascending: false }),
        (supabase as any)
          .from('customers')
          .select('id, first_name, last_name, phone')
          .in('id', customerIds),
      ]);

      if (facesResult.error) {
        throw new Error(facesResult.error.message);
      }
      if (customersResult.error) {
        throw new Error(customersResult.error.message);
      }

      const entryFaceByVisit = new Map<string, VisitFace>();
      ((facesResult.data || []) as VisitFace[]).forEach((face) => {
        if (!entryFaceByVisit.has(face.visit_id)) {
          entryFaceByVisit.set(face.visit_id, face);
        }
      });

      const customerById = new Map<string, CustomerLite>();
      ((customersResult.data || []) as CustomerLite[]).forEach((customer) => {
        customerById.set(customer.id, customer);
      });

      const baseRecords: KnownCustomerRecord[] = knownVisits.map((visit) => ({
        visit,
        customer: customerById.get(visit.customer_id),
        entryFace: entryFaceByVisit.get(visit.id),
      }));

      const entryUrls = await Promise.all(
        baseRecords.map(async (record) => getImageUrl(record.entryFace?.snapshot_path || null))
      );

      const withImages = baseRecords.map((record, index) => ({
        ...record,
        entryImageUrl: entryUrls[index],
      }));

      setRecords(withImages);
    } catch (error) {
      console.error('Error loading known customers:', error);
      toast({
        title: 'Hata',
        description: 'Tanınan müşteri kayıtları yüklenemedi.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    if (!companyId || !canAccessKnownCustomers) return;

    try {
      const allCustomers = await customerService.getAllCustomers(companyId);
      setCustomers(
        allCustomers.map((customer) => ({
          id: customer.id,
          first_name: customer.first_name,
          last_name: customer.last_name,
          phone: customer.phone,
          email: customer.email,
        }))
      );
    } catch (error) {
      console.error('Error loading customers for rematch:', error);
      toast({
        title: 'Hata',
        description: 'Müşteri listesi yüklenemedi.',
        variant: 'destructive',
      });
    }
  };

  const loadMonthlyTrend = async () => {
    if (!companyId || !canAccessKnownCustomers) return;

    setTrendLoading(true);
    try {
      const now = new Date();
      const firstMonth = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      const { data, error } = await (supabase as any)
        .from('visits')
        .select('status, entered_at, created_at')
        .eq('company_id', companyId)
        .in('status', ['LABELED', 'PENDING_LABEL'])
        .gte('created_at', firstMonth.toISOString())
        .order('created_at', { ascending: true });

      if (error) {
        throw new Error(error.message);
      }

      const monthlyMap = new Map<string, MonthlyTrendPoint>();
      const monthDates: Date[] = [];

      for (let i = 0; i < 12; i += 1) {
        const monthDate = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
        monthDates.push(monthDate);
        const key = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
        monthlyMap.set(key, {
          month: formatMonthLabel(monthDate),
          known: 0,
          unknown: 0,
        });
      }

      ((data || []) as Array<{ status: string; entered_at: string | null; created_at: string }>).forEach((visit) => {
        const raw = visit.entered_at || visit.created_at;
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) return;
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const bucket = monthlyMap.get(key);
        if (!bucket) return;
        if (visit.status === 'LABELED') bucket.known += 1;
        if (visit.status === 'PENDING_LABEL') bucket.unknown += 1;
      });

      setMonthlyTrend(monthDates.map((d) => {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return monthlyMap.get(key)!;
      }));
    } catch (error) {
      console.error('Error loading known/unknown monthly trend:', error);
      toast({
        title: 'Hata',
        description: 'Aylık trend grafiği yüklenemedi.',
        variant: 'destructive',
      });
      setMonthlyTrend([]);
    } finally {
      setTrendLoading(false);
    }
  };

  useEffect(() => {
    loadKnownCustomers();
  }, [companyId, canAccessKnownCustomers, selectedDate]);

  useEffect(() => {
    loadMonthlyTrend();
  }, [companyId, canAccessKnownCustomers]);

  useEffect(() => {
    fetchCustomers();
  }, [companyId, canAccessKnownCustomers]);

  const stats = useMemo(() => {
    const confidenceValues = records
      .map((record) => record.visit.match_confidence)
      .filter((value): value is number => typeof value === 'number');
    const qualityValues = records
      .map((record) => record.entryFace?.quality_score)
      .filter((value): value is number => typeof value === 'number');

    const uniqueCustomerCount = new Set(records.map((record) => record.visit.customer_id)).size;
    const avgConfidence = confidenceValues.length
      ? (confidenceValues.reduce((sum, value) => sum + value, 0) / confidenceValues.length) * 100
      : null;
    const avgQuality = qualityValues.length
      ? qualityValues.reduce((sum, value) => sum + value, 0) / qualityValues.length
      : null;

    return {
      total: records.length,
      uniqueCustomers: uniqueCustomerCount,
      avgConfidence,
      avgQuality,
    };
  }, [records]);

  const formatDate = (value: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('tr-TR');
  };

  const statusLabel = (status: string | null): { text: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' } => {
    switch (status) {
      case 'LABELED':
        return { text: 'Etiketli', variant: 'secondary' };
      default:
        return { text: status || '-', variant: 'outline' };
    }
  };

  const customerName = (customer?: CustomerLite) => {
    if (!customer) return 'Müşteri bulunamadı';
    const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    return fullName || 'İsimsiz Müşteri';
  };

  const handleMarkWrongCustomer = async (record: KnownCustomerRecord) => {
    const confirmed = window.confirm(
      'Bu müşteri eşleşmesi yanlış olarak işaretlensin mi?\n\nKayıt bilinmeyen müşterilere geri taşınacaktır.'
    );
    if (!confirmed) return;

    setUpdatingVisitId(record.visit.id);
    try {
      const { error } = await (supabase as any)
        .from('visits')
        .update({
          customer_id: null,
          status: 'PENDING_LABEL',
          updated_at: new Date().toISOString(),
        })
        .eq('id', record.visit.id);

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Başarılı',
        description: 'Kayıt bilinmeyen müşterilere taşındı.',
      });
      await Promise.all([loadKnownCustomers(), loadMonthlyTrend()]);
    } catch (error) {
      console.error('Error marking known visit as wrong customer:', error);
      toast({
        title: 'Hata',
        description: 'Yanlış müşteri işlemi sırasında hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingVisitId(null);
    }
  };

  const openMatchDialog = (record: KnownCustomerRecord) => {
    setSelectedRecord(record);
    setSelectedCustomerId(record.visit.customer_id);
    setSearchTerm('');
    setDialogLookalikes([]);
    setDialogLookalikeImageUrls({});
    setMatchDialogOpen(true);
    void loadDialogLookalikes(record.entryFace?.id);
  };

  const loadDialogLookalikes = async (visitFaceId?: string) => {
    if (!visitFaceId) return;

    setDialogLookalikesLoading(true);
    try {
      const { data, error } = await supabase.rpc('find_visit_lookalikes' as any, {
        p_visit_face_id: visitFaceId,
        p_limit: MAX_DIALOG_LOOKALIKE_SUGGESTIONS,
      });

      if (error) {
        throw new Error(error.message);
      }

      const topCandidates = ((data || []) as LookalikeCandidate[])
        .map((candidate) => ({
          ...candidate,
          similarity: Math.min(1, Math.max(0, Number(candidate.similarity) || 0)),
        }))
        .filter((candidate) => candidate.similarity >= LOOKALIKE_MIN_SIMILARITY)
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, MAX_DIALOG_LOOKALIKE_SUGGESTIONS);

      setDialogLookalikes(topCandidates);

      const imageUrlsByCustomer: Record<string, string> = {};
      await Promise.all(
        topCandidates.map(async (candidate) => {
          if (!candidate.template_snapshot_path) return;
          const signedUrl = await getImageUrl(candidate.template_snapshot_path);
          if (signedUrl) {
            imageUrlsByCustomer[candidate.customer_id] = signedUrl;
          }
        })
      );
      setDialogLookalikeImageUrls(imageUrlsByCustomer);
    } catch (error) {
      console.error('Error loading lookalike suggestions for rematch:', error);
      setDialogLookalikes([]);
      setDialogLookalikeImageUrls({});
    } finally {
      setDialogLookalikesLoading(false);
    }
  };

  const handleMatchAnotherCustomer = async () => {
    if (!selectedRecord || !selectedCustomerId) return;

    if (selectedCustomerId === selectedRecord.visit.customer_id) {
      toast({
        title: 'Bilgi',
        description: 'Aynı müşteri seçili. Lütfen farklı bir müşteri seçin.',
      });
      return;
    }

    setUpdatingVisitId(selectedRecord.visit.id);
    try {
      await VisitService.linkVisitToCustomer(selectedRecord.visit.id, selectedCustomerId);
      toast({
        title: 'Başarılı',
        description: 'Kayıt yeni müşteri ile eşleştirildi.',
      });
      setMatchDialogOpen(false);
      setSelectedRecord(null);
      await Promise.all([loadKnownCustomers(), loadMonthlyTrend()]);
    } catch (error) {
      console.error('Error rematching known visit:', error);
      toast({
        title: 'Hata',
        description: 'Yeni müşteri ile eşleştirme yapılamadı.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingVisitId(null);
    }
  };

  const searchableCustomers = useMemo(
    () =>
      customers.map((customer) => ({
        customer,
        name: normalizeSearchText(customerName(customer)),
        email: normalizeSearchText(customer.email || ''),
        phoneRaw: normalizeSearchText(customer.phone || ''),
        phoneDigits: normalizePhoneForSearch(customer.phone || ''),
      })),
    [customers]
  );

  const filteredCustomers = useMemo(() => {
    const normalizedSearch = normalizeSearchText(searchTerm);
    const normalizedSearchDigits = normalizePhoneForSearch(searchTerm);

    if (!normalizedSearch && !normalizedSearchDigits) {
      return customers.slice(0, MAX_CUSTOMER_RESULTS);
    }

    return searchableCustomers
      .filter(({ name, email, phoneRaw, phoneDigits }) => {
        const textMatch =
          (normalizedSearch && (name.includes(normalizedSearch) || email.includes(normalizedSearch) || phoneRaw.includes(normalizedSearch))) ||
          false;
        const digitMatch =
          (normalizedSearchDigits && phoneDigits.includes(normalizedSearchDigits)) || false;
        return textMatch || digitMatch;
      })
      .map(({ customer }) => customer)
      .slice(0, MAX_CUSTOMER_RESULTS);
  }, [customers, searchableCustomers, searchTerm]);

  if (authLoading || adminLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Tanınan müşteriler yükleniyor...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!canAccessKnownCustomers) {
    return null;
  }

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Tanınan Müşteriler</h1>
              <p className="text-muted-foreground">
                Sistem tarafından otomatik tanınan müşteri ziyaretleri
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-[180px]"
            />
            <Button
              variant="outline"
              onClick={() => setSelectedDate(getLocalDateInputValue())}
            >
              Bugün
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                loadKnownCustomers();
                loadMonthlyTrend();
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Yenile
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Aylık Bilinen / Bilinmeyen Giriş Trendi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trendLoading ? (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                Grafik yükleniyor...
              </div>
            ) : monthlyTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="known" name="Bilinen" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="unknown" name="Bilinmeyen" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                Trend verisi bulunamadı
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Toplam Tanıma</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Farklı Müşteri</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">{stats.uniqueCustomers}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Ortalama Kalite</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">
                {stats.avgQuality !== null ? stats.avgQuality.toFixed(2) : '-'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Ortalama Güven</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold">
                {stats.avgConfidence !== null ? `%${stats.avgConfidence.toFixed(1)}` : '-'}
              </p>
            </CardContent>
          </Card>
        </div>

        {records.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <UserCheck className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Tanınan müşteri kaydı bulunamadı</h3>
              <p className="text-muted-foreground text-center">
                Sistem henüz otomatik tanıma üretmemiş olabilir veya tüm kayıtlar manuel etiketlenmiş olabilir.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {records.map((record) => {
              const visitStatus = statusLabel(record.visit.status);
              return (
                <Card key={record.visit.id} className="overflow-hidden">
                  <div className="aspect-square overflow-hidden relative bg-muted">
                    {record.entryImageUrl ? (
                      <img
                        src={record.entryImageUrl}
                        alt="Giriş"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                        Giriş görseli yok
                      </div>
                    )}
                    <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                      <LogIn className="w-3 h-3" /> Giriş
                    </span>
                  </div>
                  <CardContent className="p-4 space-y-3">
                    <div className="space-y-1">
                      <p className="font-semibold text-base">{customerName(record.customer)}</p>
                      <p className="text-xs text-muted-foreground">{record.customer?.phone || '-'}</p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={visitStatus.variant}>{visitStatus.text}</Badge>
                      {typeof record.visit.match_confidence === 'number' && (
                        <Badge variant="outline">
                          Güven %{(record.visit.match_confidence * 100).toFixed(1)}
                        </Badge>
                      )}
                    </div>

                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>Giriş: {formatDate(record.visit.entered_at || record.visit.created_at)}</p>
                    </div>

                    <div className="grid grid-cols-1 gap-2 pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openMatchDialog(record)}
                        disabled={updatingVisitId === record.visit.id}
                      >
                        {updatingVisitId === record.visit.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-2" />
                        )}
                        Başka Müşteri ile Eşleştir
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleMarkWrongCustomer(record)}
                        disabled={updatingVisitId === record.visit.id}
                      >
                        {updatingVisitId === record.visit.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <XCircle className="w-4 h-4 mr-2" />
                        )}
                        Yanlış Müşteri
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={matchDialogOpen} onOpenChange={setMatchDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Başka Müşteri ile Eşleştir</DialogTitle>
            <DialogDescription>
              Bu ziyareti farklı bir müşteriyle eşleştirin.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2 md:col-span-1">
              <p className="text-sm font-medium">En Benzer 3 Müşteri</p>
              <div className="space-y-2">
                {dialogLookalikesLoading ? (
                  <div className="h-24 border rounded-lg flex items-center justify-center text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Öneriler yükleniyor...
                  </div>
                ) : dialogLookalikes.length === 0 ? (
                  <div className="h-24 border rounded-lg flex items-center justify-center text-sm text-muted-foreground px-3 text-center">
                    Bu kayıt için benzer müşteri önerisi bulunamadı.
                  </div>
                ) : (
                  dialogLookalikes.map((candidate) => {
                    const candidateName = `${candidate.customer_first_name || ''} ${candidate.customer_last_name || ''}`.trim() || 'İsimsiz Müşteri';
                    const isSelected = selectedCustomerId === candidate.customer_id;
                    return (
                      <button
                        key={candidate.template_id}
                        type="button"
                        className={`w-full text-left p-2 border rounded-lg hover:bg-muted transition ${
                          isSelected ? 'bg-primary/10 border-primary/40' : ''
                        }`}
                        onClick={() => setSelectedCustomerId(candidate.customer_id)}
                      >
                        <div className="flex items-center gap-2">
                          {dialogLookalikeImageUrls[candidate.customer_id] ? (
                            <img
                              src={dialogLookalikeImageUrls[candidate.customer_id]}
                              alt={candidateName}
                              className="w-9 h-9 rounded-md object-cover border shrink-0"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-md border bg-muted shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{candidateName}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {candidate.customer_phone || 'Telefon yok'}
                            </p>
                          </div>
                          <Badge variant="secondary" className="ml-auto text-xs">
                            %{(candidate.similarity * 100).toFixed(0)}
                          </Badge>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="space-y-4 md:col-span-2">
              <Input
                placeholder="Müşteri ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <div className="max-h-72 overflow-y-auto border rounded-lg">
                {filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className={`p-3 cursor-pointer hover:bg-muted border-b last:border-b-0 ${
                      selectedCustomerId === customer.id ? 'bg-primary/10' : ''
                    }`}
                    onClick={() => setSelectedCustomerId(customer.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{customerName(customer)}</div>
                        {customer.email && (
                          <div className="text-sm text-muted-foreground">{customer.email}</div>
                        )}
                        {customer.phone && (
                          <div className="text-sm text-muted-foreground">{customer.phone}</div>
                        )}
                      </div>
                      {selectedCustomerId === customer.id && (
                        <Check className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setMatchDialogOpen(false)}
            >
              İptal
            </Button>
            <Button
              className="flex-1"
              onClick={handleMatchAnotherCustomer}
              disabled={
                !selectedRecord ||
                !selectedCustomerId ||
                selectedCustomerId === selectedRecord.visit.customer_id ||
                updatingVisitId === selectedRecord.visit.id
              }
            >
              {selectedRecord && updatingVisitId === selectedRecord.visit.id ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Eşleşmeyi Güncelle
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default KnownCustomers;
